<template>
    <div class="reservation-trends-panel">
        <h3 class="panel-title poppinsSemiBold" :style="{ fontSize: normalizeFontSize(15) }">
            {{ $t('dashboard.trends.title') }}
        </h3>

        <div class="trends-list">
            <div v-for="(item, index) in trendItems" :key="index" class="trend-item">
                <div class="trend-info">
                    <span class="trend-label">{{ item.label }}</span>
                    <span class="trend-value">{{ item.count }}</span>
                </div>

                <div class="trend-weather" v-if="item.weather">
                    <span class="weather-temp">{{ Math.round(item.weather.temp) }}°</span>
                    <img :src="getWeatherIconUrl(item.weather.icon)" :alt="item.weather.description" class="weather-icon" />
                </div>

                <!-- Sparkline chart hidden for now - to be implemented later
                <div class="trend-sparkline">
                    <svg viewBox="0 0 143 37" class="sparkline-svg" :class="item.trend">
                        <path
                            :d="generateSparklinePath(item.hourlyData)"
                            fill="none"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
                -->
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
export default {
    name: 'ReservationTrends',
    props: {
        trendsData: {
            type: Array,
            default: () => [],
        },
        weatherData: {
            type: Object,
            default: null,
        },
    },
    computed: {
        trendItems() {
            if (this.trendsData && this.trendsData.length > 0) {
                return this.trendsData.map((day, index) => ({
                    label: day.label,
                    count: day.count,
                    hourlyData: day.hourlyData || [],
                    weather: this.getWeatherForDate(day.date),
                    trend: this.getTrendDirection(day.hourlyData),
                    isOpen: day.isOpen,
                }))
            }
            return []
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        generateSparklinePath(hourlyData) {
            if (!hourlyData || hourlyData.length === 0) {
                return 'M0 18.5L143 18.5'
            }

            const width = 143
            const height = 37
            const padding = 2
            const maxValue = Math.max(...hourlyData, 1)
            const points = hourlyData.length

            const xStep = (width - padding * 2) / (points - 1 || 1)

            const pathPoints = hourlyData.map((value, index) => {
                const x = padding + index * xStep
                const y = height - padding - (value / maxValue) * (height - padding * 2)
                return `${x} ${y}`
            })

            return `M${pathPoints.join('L')}`
        },
        getTrendDirection(hourlyData) {
            if (!hourlyData || hourlyData.length < 2) return 'up'
            const firstHalf = hourlyData.slice(0, Math.floor(hourlyData.length / 2))
            const secondHalf = hourlyData.slice(Math.floor(hourlyData.length / 2))
            const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / (firstHalf.length || 1)
            const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / (secondHalf.length || 1)
            return secondAvg >= firstAvg ? 'up' : 'down'
        },
        getWeatherForDate(dateStr) {
            if (!this.weatherData?.list || !dateStr) return null

            const targetDate = new Date(dateStr)
            targetDate.setHours(12, 0, 0, 0)
            const targetTime = targetDate.getTime() / 1000

            let closest = this.weatherData.list[0]
            let minDiff = Math.abs(closest.dt - targetTime)

            for (const entry of this.weatherData.list) {
                const diff = Math.abs(entry.dt - targetTime)
                if (diff < minDiff) {
                    minDiff = diff
                    closest = entry
                }
            }

            if (closest) {
                return {
                    temp: closest.main?.temp,
                    icon: closest.weather?.[0]?.icon,
                    description: closest.weather?.[0]?.description,
                }
            }
            return null
        },
        getWeatherIconUrl(icon) {
            return `https://openweathermap.org/img/wn/${icon}@2x.png`
        },
    },
}
</script>

<style scoped>
.reservation-trends-panel {
    width: 100%;
    border-radius: 20px;
    background: linear-gradient(127deg, rgba(6, 11, 40, 0.74) 28.26%, rgba(14, 21, 58, 0.71) 91.2%);
    backdrop-filter: blur(60px);
    padding: 22px 28px;
    overflow: hidden;
}

.panel-title {
    color: #fff;
    margin: 0 0 20px 0;
}

.trends-list {
    display: flex;
    flex-direction: column;
}

.trend-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

.trend-item:last-child {
    border-bottom: none;
}

.trend-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 130px;
}

.trend-label {
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 400;
}

.trend-value {
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 19px;
    font-weight: 800;
}

.trend-weather {
    display: flex;
    align-items: center;
    gap: 5px;
}

.weather-temp {
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 18px;
    font-weight: 600;
}

.weather-icon {
    width: 45px;
    height: 45px;
}

.trend-sparkline {
    flex: 1;
    max-width: 143px;
    height: 37px;
}

.sparkline-svg {
    width: 100%;
    height: 100%;
}

.sparkline-svg.up path {
    stroke: #02bd7f;
}

.sparkline-svg.down path {
    stroke: #c52b21;
}
</style>
